import { formatLocalDatetimeForWire } from '@/shared/lib/date-format';
import type {
  CalendarDevice,
  DeviceAppointmentProfile,
  DeviceBookingRequest,
  DeviceRecurringRequest,
  LocationModel,
} from '@/types/scheduling-api';
import type { AppointmentFormValues } from '@/types/appointment-form';

/**
 * Device booking payload (Scheduling PR #1964, S12) — the practitioner
 * `BookAppointment` body minus everything practitioner-shaped.
 *
 * Contract, verified against `CreateAppointmentCommandValidator` on the
 * branch:
 *   - `deviceId` + `deviceProfileId` mark the booking as device-first.
 *   - `start` only; the server derives `end` from the product's minutes.
 *   - NEVER `practitionerId` (→ DeviceBookingHasProvider), `resourceIds`
 *     (→ DeviceBookingHasResourceIds), `reminderProfile` (ignored — reminders
 *     are practitioner-keyed). No `recurring` here either: PROD-2383's course is
 *     added by the panel, for `BookResource`, from the shared configurator.
 *   - MUST be priceable from SOMETHING: the product's own `serviceId`
 *     (PROD-2165) or its `billingProfileId` (PROD-2154), both of which the
 *     server reads for itself, or a non-empty `addedServices` (service ids)
 *     → otherwise DeviceBookingNeedsBillingProfile.
 *   - `location` stays required (the invoice is priced by location).
 */
export interface DeviceBookingDraft {
  device: CalendarDevice;
  product: DeviceAppointmentProfile;
  patientId: string;
  /** Local instant the user picked; formatted no-Z for the wire. */
  start: Date;
  location: LocationModel;
  /** Catalog service ids that make the booking invoiceable. */
  serviceIds: readonly string[];
  /**
   * A billing profile the CALLER chose, when a picker for one ever exists.
   *
   * Never the device product's own `billingProfileId`: Scheduling's
   * `ResolveBillingProfileId` treats a sent value as precedence, not a merge
   * (requested wins, else the product's), so echoing a snapshot from a cached
   * product list would override a billing profile the practice has since
   * changed. The product's default is READ to decide whether services are
   * required, and left for the server to resolve on its own fresh read.
   */
  billingProfileId?: string | null;
  comment?: string | null;
}

export interface DeviceBookingContext {
  organizationId: string;
  practiceId: string;
  createdBy: string;
}

/** Default appointment type when the product names none (mirrors the practitioner transform). */
const ROUTINE = { code: 'ROUTINE', display: 'Routine', definition: null, system: null };

/**
 * Display names for the appointment-type codes a device product can name.
 * `ResourceAppointmentProfileResponse.appointmentTypeId` is a CODE
 * (`dbo.AppointmentTypes.Code`) with no display beside it, and shipping the
 * code as the display put "FOLLOWUP" on screen wherever the appointment type
 * is rendered (review #7). These are the same three the booking picker
 * offers (`provider-selection-view.tsx`); an unknown code still falls back to
 * itself rather than being dropped.
 */
const APPOINTMENT_TYPE_DISPLAY: Record<string, string> = {
  ROUTINE: 'Routine',
  FOLLOWUP: 'Follow Up',
  WALKIN: 'Walk In',
};

/** The code's display name when we know one, else the code itself. */
export function appointmentTypeDisplay(code: string): string {
  return APPOINTMENT_TYPE_DISPLAY[code.trim().toUpperCase()] ?? code;
}

/**
 * The location a device booking is priced at (PROD-2193).
 *
 * Andrew, 2026-09-13: "in the booking remove the location dropdown as of
 * course it will be office". A device is a machine standing in the practice —
 * "Home" and "Virtual" are delivery modes a machine cannot have, so the
 * control was a question with exactly one right answer. The FIELD did not go
 * away: `location` is still required on the wire because the invoice is priced
 * by location, so the rail resolves it instead of asking.
 *
 * The rule, in order:
 *   1. the location NAMED "Office" (case-insensitively, trimmed) — the org
 *      locations are delivery-mode categories, which is why
 *      `location-selector.tsx` maps Office / Home / Virtual to icons;
 *   2. else the FIRST location — that same selector falls back to a `MapPin`
 *      for any other name, which proves an org can define its own, and a
 *      device booking must not send null because one did;
 *   3. else null, so a practice with NO locations fails the caller's guard
 *      loudly rather than booking something that cannot be invoiced.
 */
export function resolveDeviceLocation<T extends { name: string }>(
  locations: readonly T[],
): T | null {
  const office = locations.find((l) => (l.name ?? '').trim().toLowerCase() === 'office');
  return office ?? locations[0] ?? null;
}

export function buildDeviceBookingRequest(
  draft: DeviceBookingDraft,
  ctx: DeviceBookingContext,
): DeviceBookingRequest {
  const typeCode = draft.product.appointmentTypeId?.trim();
  return {
    deviceId: draft.device.id,
    deviceProfileId: draft.product.id,
    patientId: draft.patientId,
    practiceId: ctx.practiceId,
    organizationId: ctx.organizationId,
    start: formatLocalDatetimeForWire(draft.start),
    appointmentType: typeCode
      ? { code: typeCode, display: appointmentTypeDisplay(typeCode), definition: null, system: null }
      : ROUTINE,
    // No practitioner → no speciality; same empty SNOMED shape the
    // practitioner transform falls back to.
    specialty: {},
    location: {
      id: draft.location.id,
      name: draft.location.name,
      imageUrl: draft.location.imageUrl,
      isArchived: draft.location.isArchived ?? false,
      description: draft.location.description ?? '',
    },
    reasonCode: { code: '', display: '', definition: '', system: null },
    roomId: draft.device.roomId ?? undefined,
    priority: 0,
    appointmentProfile: draft.billingProfileId ? { billingProfileId: draft.billingProfileId } : {},
    // Only the services the user picked — the product's own `serviceId` is
    // NOT echoed here. The server resolves it from the product itself and
    // unions it in, so sending a cached copy would pin a price the practice
    // may since have changed. The EDIT builder (`device-edit-request.ts`)
    // deliberately does the OPPOSITE and sends it: there the server's re-read
    // can miss on an archived product (PROD-2170) and Billing would delete the
    // line. Read that comment before making these two agree.
    addedServices: [...draft.serviceIds],
    addedIndividualServices: [],
    sponsorType: 'Self-pay',
    sponsor: null,
    flagList: [],
    isCreatedByPatient: false,
    isRelatedPerson: false,
    createdBy: ctx.createdBy,
    comment: draft.comment?.trim() ? draft.comment.trim() : null,
    description: null,
    // Zeroed invoice snapshot — the same field the practitioner transform
    // ships; the server prices the real invoice from the services.
    invoice: {
      grossTotal: 0,
      discountTotal: 0,
      netTotal: 0,
      patientPayable: 0,
      deposited: 0,
      sponsorOutstanding: 0,
      sponsorPaymentTotal: 0,
      patientPaymentStatus: 0,
      patientOutstanding: 0,
      excludedTotal: 0,
      patientShareTotal: 0,
      sponsorAmountTotal: 0,
    },
  };
}

/**
 * Client-side mirror of the invoice rule, so the Book button can explain itself.
 *
 * Scheduling refuses a device booking only when it can find NO price at all:
 * no service on the product, no billing profile (the caller's or the
 * product's), and no added services. Every one of those three is enough on its
 * own, so this must accept all three — a client stricter than the server blocks
 * a booking the server would have taken.
 *
 * `productServiceId` is the arm PROD-2165 added and PROD-2175 restored here: a
 * product priced by its own service is invoiceable with nothing else attached,
 * which is the entire point of pointing a product at a service.
 */
export function deviceBookingIsInvoiceable(
  draft: Pick<DeviceBookingDraft, 'serviceIds' | 'billingProfileId'> & {
    /** The chosen product's own priced service, when it has one. */
    productServiceId?: string | null;
  },
): boolean {
  return (
    Boolean(draft.billingProfileId) ||
    Boolean(draft.productServiceId) ||
    draft.serviceIds.length > 0
  );
}

/**
 * PROD-2383 — the configurator's recurrence, as the device route takes it. The
 * provider body ships each occurrence's full slot (provider, practice,
 * appointment type…); a device session is a start time and nothing else,
 * because the product owns its window. `days` only for "Weeks", as the
 * provider transform sends it.
 */
export function toDeviceRecurring(
  recurring: AppointmentFormValues['recurring'] | null | undefined,
): DeviceRecurringRequest | null {
  if (!recurring) return null;
  const slots = (recurring.slots ?? [])
    .map((s) => s.start)
    .filter((start): start is string => !!start)
    .map((start) => ({ start }));
  if (slots.length === 0) return null;
  return {
    startDate: recurring.startDate,
    endDate: recurring.endDate,
    repeatEvery: recurring.repeatEvery,
    numberOfRepeat: recurring.numberOfRepeat,
    period: recurring.period,
    days: recurring.period === 'Weeks' ? (recurring.days ?? []) : null,
    slots,
  };
}
